"use client"

import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import axios from "axios"
import { Transition } from "@headlessui/react"
import { motion } from "framer-motion"
import { FiMapPin, FiBriefcase, FiCalendar, FiSmartphone, FiMail, FiClock, FiBookmark, FiShare2 } from "react-icons/fi"
import { uploadFile, deleteFile } from "../utils/api" // adjust path as needed
import { BiRupee } from "react-icons/bi"
import { PiBriefcaseMetalDuotone } from "react-icons/pi" // for experience
import { ClipLoader } from "react-spinners"

function classNames(...classes) {
  return classes.filter(Boolean).join(" ")
}

const Jobdetail = () => {
  const { id } = useParams()
  const [job, setJob] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [otherJobs, setOtherJobs] = useState([])
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    resume: null, // local File reference
    resumeUrl: "",
    publicId: "",
  })
  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    email: ""
  })
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    const fetchJob = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs/${id}`)
        setJob(res.data)
      } catch (err) {
        console.error("Failed to fetch job:", err)
      }
    }

    const fetchOtherJobs = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/jobs`)
        setOtherJobs(res.data.filter((item) => item._id !== id))
      } catch (err) {
        console.error("Failed to fetch other jobs:", err)
      }
    }

    fetchJob()
    fetchOtherJobs()
  }, [id])

  // Validate name field (only alphabets and spaces)
  const validateName = (name) => {
    const regex = /^[a-zA-Z\s]*$/
    if (!regex.test(name)) {
      return "Name should only contain alphabets"
    }
    return ""
  }

  // Validate phone field (exactly 10 digits)
  const validatePhone = (phone) => {
    const regex = /^\d{10}$/
    if (!regex.test(phone)) {
      return "Phone number should be 10 digits"
    }
    return ""
  }

  // Validate email field
  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!regex.test(email)) {
      return "Please enter a valid email"
    }
    return ""
  }

  // handle input changes including file upload
  const handleChange = async (e) => {
    const { name, value, files } = e.target

    // Validate fields before updating state
    if (name === "name") {
      setErrors(prev => ({ ...prev, name: validateName(value) }))
    } else if (name === "phone") {
      setErrors(prev => ({ ...prev, phone: validatePhone(value) }))
    } else if (name === "email") {
      setErrors(prev => ({ ...prev, email: validateEmail(value) }))
    }

    if (name === "resume" && files?.[0]) {
      // if there's an existing uploaded file, delete it
      if (formData.publicId) {
        try {
          await deleteFile(formData.publicId)
        } catch (delErr) {
          console.warn("Failed to delete previous file:", delErr)
        }
      }

      const file = files[0]
      setUploading(true)
      try {
        // upload to server/cloud
        const data = await uploadFile(file)
        setFormData((prev) => ({
          ...prev,
          resume: file,
          resumeUrl: data.url,
          publicId: data.publicId,
        }))
      } catch (uploadErr) {
        console.error("Error uploading resume:", uploadErr)
        alert("Failed to upload resume. Please try again.")
      } finally {
        setUploading(false)
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validate all fields before submission
    const nameError = validateName(formData.name)
    const phoneError = validatePhone(formData.phone)
    const emailError = validateEmail(formData.email)

    setErrors({
      name: nameError,
      phone: phoneError,
      email: emailError
    })

    if (nameError || phoneError || emailError) {
      return
    }

    if (!formData.resumeUrl) {
      alert("Please upload your resume before submitting.")
      return
    }

    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      resumeUrl: formData.resumeUrl,
      jobId: id,
      userId: job.userId,
      company: job.company,
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/jobapplied`, payload)
      setFormData({ name: "", email: "", phone: "", resume: null, resumeUrl: "", publicId: "" })
      setShowSuccessPopup(true)
      setTimeout(() => setShowSuccessPopup(false), 5000)
      setIsOpen(false)
    } catch (err) {
      console.error("Error submitting application:", err)
      alert("Failed to submit application.")
    }
  }

  const SuccessPopup = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0 }}
        className="bg-white p-8 rounded-2xl shadow-2xl text-center"
      >
        <motion.svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-16 w-16 text-green-500 mx-auto mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <motion.path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </motion.svg>
        <h3 className="text-lg font-semibold text-gray-800">Application Submitted!</h3>
        <p className="text-sm text-gray-600">Your application has been successfully sent.</p>
      </motion.div>
    </div>
  )

  if (!job) return <div className="text-center py-20 text-gray-500">Loading job details...</div>

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Job Detail Section */}
          <div className="flex-1 lg:order-1">
            {/* Job Header Card with Apply Button */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-lg border border-gray-200 flex items-center justify-center bg-gray-50 flex-shrink-0">
                    <img
                      src={job.logo || "https://via.placeholder.com/60"}
                      alt={job.company}
                      className="w-12 h-12 rounded-lg object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{job.position}</h1>
                    <h2 className="text-lg text-gray-700 font-medium mb-3">{job.company}</h2>
                    <div className="flex flex-wrap gap-2">
                      {job.isNew && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          New
                        </span>
                      )}
                      {job.featured && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Featured
                        </span>
                      )}
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {job.contract}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* Apply Button in Header */}
              <div className="border-t border-gray-200 pt-4">
                <button
                  onClick={() => setIsOpen(true)}
                  className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-200"
                >
                  Apply for this Job
                </button>
              </div>
            </div>

            {/* Job Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                    <FiBriefcase className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Role</p>
                    <p className="text-sm font-medium text-gray-900">{job.role}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                    <FiMapPin className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="text-sm font-medium text-gray-900">{job.district}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center">
                    <BiRupee className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Salary</p>
                    <p className="text-sm font-medium text-gray-900">₹{job.salary || "Not disclosed"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center">
                    <PiBriefcaseMetalDuotone className="w-4 h-4 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Experience</p>
                    <p className="text-sm font-medium text-gray-900">{job.experience} years</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                    <FiCalendar className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Posted</p>
                    <p className="text-sm font-medium text-gray-900">{new Date(job.postedAt).toDateString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center">
                    <FiClock className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Join Date</p>
                    <p className="text-sm font-medium text-gray-900">{new Date(job.dateOfJoining).toDateString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Skills Required */}
            {job.tools && job.tools.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills Required</h3>
                <div className="flex flex-wrap gap-2">
                  {job.tools.map((tool, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Languages Required */}
            {job.languages && job.languages.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Languages Required</h3>
                <div className="flex flex-wrap gap-2">
                  {job.languages.map((language, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200"
                    >
                      {language}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Job Description */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Description</h3>
              {job.shortDescription && (
                <div className="mb-4 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                  <p className="text-blue-800 font-medium">{job.shortDescription}</p>
                </div>
              )}
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">{job.longDescription}</p>
              </div>
            </div>

            {/* Contact Information */}
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                    <FiSmartphone className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="text-sm font-medium text-gray-900">{job.contactPhone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                    <FiMail className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="text-sm font-medium text-gray-900">{job.contactEmail}</p>
                  </div>
                </div>
              </div>
            </div> */}
          </div>

          {/* Fixed Similar Jobs Sidebar */}
          <div className="w-full lg:w-80 lg:order-2">
            <div className="lg:sticky lg:top-24">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Similar Jobs</h3>
                <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {otherJobs.slice(0, 10).map((otherJob) => (
                    <div
                      key={otherJob._id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <img
                          src={otherJob.logo || "https://via.placeholder.com/40"}
                          alt={otherJob.company}
                          className="w-10 h-10 rounded-lg object-cover border border-gray-200 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 truncate">{otherJob.position}</h4>
                          <p className="text-xs text-gray-600 truncate">{otherJob.company}</p>
                        </div>
                      </div>
                      <div className="space-y-2 mb-3">
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <FiMapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{otherJob.district}</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <BiRupee className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{otherJob.salary || "Not disclosed"}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1 mb-3">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">{otherJob.role}</span>
                        <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded">{otherJob.level}</span>
                      </div>
                      <Link
                        to={`/jd/${otherJob._id}`}
                        className="block w-full text-center bg-gray-100 text-gray-700 text-xs py-2 rounded hover:bg-gray-200 transition-colors"
                      >
                        View Details
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Transition show={isOpen}>
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-end sm:items-center justify-center"
          onClick={() => setIsOpen(false)}
        >
          <Transition.Child
            enter="transition duration-300 transform"
            enterFrom="translate-y-full opacity-0"
            enterTo="translate-y-0 opacity-100"
            leave="transition duration-200 transform"
            leaveFrom="translate-y-0 opacity-100"
            leaveTo="translate-y-full opacity-0"
          >
            <div
              className="bg-white w-full max-w-md p-6 rounded-t-2xl sm:rounded-2xl shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Apply for this Job</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    pattern="[A-Za-z\s]+"
                    title="Name should only contain alphabets"
                    className={`w-full px-4 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    onKeyPress={e => {
                      if (!/[a-zA-Z\s]/.test(e.key)) {
                        e.preventDefault()
                      }
                    }}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </div>

                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                </div>

                <div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    maxLength="10"
                    className={`w-full px-4 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    onInput={e => {
                      // Only allow numeric digits for phone input
                      e.target.value = e.target.value.replace(/[^0-9]/g, "")
                    }}
                  />
                  {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone}</p>}
                </div>

                <div>
                  <input
                    type="file"
                    name="resume"
                    onChange={handleChange}
                    required
                    className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:border-0 file:bg-blue-600 file:text-white file:rounded-lg hover:file:bg-blue-700"
                  />
                </div>

                {/* Show loader if uploading */}
                {uploading && (
                  <div className="flex justify-center py-2">
                    <ClipLoader size={28} color="#2563eb" />
                  </div>
                )}
                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setIsOpen(false)}
                    className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={uploading || errors.name || errors.phone || errors.email}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    {uploading ? (
                      <span className="flex items-center gap-2">
                        <ClipLoader size={18} color="#fff" />
                        Uploading...
                      </span>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Transition>

      {showSuccessPopup && <SuccessPopup />}
    </div>
  )
}

export default Jobdetail
